import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { delay, Observable, of, throwError } from 'rxjs';
import {
  mockChatReply,
  mockChatReplyFollowUp,
  mockCitationlessReply,
  mockUnresolvedReply,
} from './mock-responses.const';
import { MOCK_CONVERSATION_ID, MOCK_FLAG_ON, MockScenario, MockStorageKey } from './mock-scenarios.const';

const MOCK_DELAY_MS: Readonly<number> = 1_000;
const SLOW_REPLY_DELAY_MS: Readonly<number> = 7_000;

const CHAT_PATH: Readonly<string> = '/ml6/conversation';
const PROMPT_PATH: Readonly<string> = '/ml6/send-message';
const ESCALATE_SUFFIX: Readonly<string> = '/escalate';
const FEEDBACK_SUFFIX: Readonly<string> = '/feedback';

@Injectable()
export class MockChatApiInterceptor implements HttpInterceptor {
  private turnIndex: number = 0;

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isChatRequest(req.url)) return next.handle(req);

    const scenario: string = this.scenario();
    const delayMs: number = this.delayMs();

    if (scenario === MockScenario.Forbidden) {
      return throwError(() => new HttpErrorResponse({ url: req.url, status: 403, statusText: 'Forbidden' })).pipe(
        delay(delayMs),
      );
    }

    if (scenario === MockScenario.SessionExpired) {
      return throwError(() => new HttpErrorResponse({ url: req.url, status: 401, statusText: 'Unauthorized' })).pipe(
        delay(delayMs),
      );
    }

    if (req.url.endsWith(FEEDBACK_SUFFIX)) {
      return of(new HttpResponse({ status: 200, body: null })).pipe(delay(delayMs));
    }

    if (req.url.endsWith(ESCALATE_SUFFIX)) {
      if (scenario === MockScenario.EscalateError) {
        return throwError(() => new HttpErrorResponse({ url: req.url, status: 500, statusText: 'Server Error' })).pipe(
          delay(delayMs),
        );
      }
      return of(new HttpResponse({ status: 200, body: null })).pipe(delay(delayMs));
    }

    if (req.url.endsWith(PROMPT_PATH)) {
      if (scenario === MockScenario.PromptError) {
        return throwError(() => new HttpErrorResponse({ url: req.url, status: 500, statusText: 'Server Error' })).pipe(
          delay(delayMs),
        );
      }
      const body: unknown = this.replyForPrompt(scenario);
      this.turnIndex += 1;
      const replyDelayMs: number = scenario === MockScenario.SlowReply ? SLOW_REPLY_DELAY_MS : delayMs;
      return of(new HttpResponse({ status: 200, body })).pipe(delay(replyDelayMs));
    }

    if (req.url.endsWith(CHAT_PATH)) {
      this.turnIndex = 0;
      return of(new HttpResponse({ status: 200, body: { conversationId: MOCK_CONVERSATION_ID } })).pipe(delay(delayMs));
    }

    return next.handle(req);
  }

  private isChatRequest(url: string): boolean {
    return url.includes('/ml6/');
  }

  private scenario(): string {
    try {
      return sessionStorage.getItem(MockStorageKey.Scenario) ?? '';
    } catch {
      return '';
    }
  }

  private delayMs(): number {
    try {
      return sessionStorage.getItem(MockStorageKey.Delay) === MOCK_FLAG_ON ? MOCK_DELAY_MS : 0;
    } catch {
      return 0;
    }
  }

  private replyForPrompt(scenario: string): unknown {
    if (scenario === MockScenario.Unresolved) return mockUnresolvedReply;
    if (scenario === MockScenario.Citationless) return mockCitationlessReply;
    return this.turnIndex === 0 ? mockChatReply : mockChatReplyFollowUp;
  }
}
